import { $applyNodeReplacement, createState, $getState, $setState, DecoratorNode } from 'lexical'
import { canonicalizeMediaUrl } from '@/lib/url'

// kind and status can change over time, so we need to store them in states
const kindState = createState('kind', {
  parse: (value) => (typeof value === 'string' ? value : 'unknown')
})

const statusState = createState('status', {
  parse: (value) => (typeof value === 'string' ? value : 'idle')
})

const widthState = createState('width', {
  parse: (value) => (typeof value === 'number' ? value : null)
})

const heightState = createState('height', {
  parse: (value) => (typeof value === 'number' ? value : null)
})

const srcSetState = createState('srcSet', {
  parse: (value) => (typeof value === 'string' ? value : null)
})

const bestResSrcState = createState('bestResSrc', {
  parse: (value) => (typeof value === 'string' ? value : null)
})

function $convertMediaElement (domNode) {
  let src, alt, title, width, height, kind, srcSet, bestResSrc, maxWidth, autolink

  if (domNode instanceof window.HTMLImageElement || domNode instanceof window.HTMLVideoElement) {
    ({ alt, title, src, width, height, srcset: srcSet } = domNode)
    kind = domNode instanceof window.HTMLImageElement ? 'image' : 'video'
  } else if (domNode instanceof window.HTMLSpanElement && domNode.hasAttribute('data-sn-media-src')) {
    src = domNode.getAttribute('data-sn-media-src')
    kind = domNode.getAttribute('data-sn-media-kind') || 'unknown'
    alt = domNode.getAttribute('data-sn-media-alt') || ''
    title = domNode.getAttribute('data-sn-media-title') || ''
    srcSet = domNode.getAttribute('data-sn-media-srcset') || null
    bestResSrc = domNode.getAttribute('data-sn-media-bestres') || null
    const maxWidthAttr = domNode.getAttribute('data-sn-media-maxwidth')
    maxWidth = maxWidthAttr ? parseInt(maxWidthAttr, 10) : undefined
    autolink = domNode.getAttribute('data-sn-media-autolink') === 'true'
    const style = domNode.style
    width = style.getPropertyValue('--width')
    height = style.getPropertyValue('--height')
    width = width ? parseInt(width, 10) : null
    height = height ? parseInt(height, 10) : null
  } else {
    return null
  }

  // never let a signed imgproxy preview url enter editor state via DOM paste
  src = canonicalizeMediaUrl(src)

  const node = $createMediaNode({ src, alt, title, width, height, srcSet, bestResSrc, maxWidth, autolink })
  $setState(node, kindState, kind)
  $setState(node, statusState, 'done')
  return { node }
}

/** builds the media wrapper span shared by createDOM/exportDOM — every
 * attribute $convertMediaElement reads to losslessly reconstruct the node
 * from HTML is written here */
function createMediaSpan (node, theme) {
  const kind = node.getKind()
  const span = document.createElement('span')
  span.className = theme?.media
  span.setAttribute('data-sn-media-kind', kind)
  span.setAttribute('data-sn-media-src', node.getSrc())
  const alt = node.getAlt()
  const title = node.getTitle()
  const srcSet = node.getSrcSet()
  const bestResSrc = node.getBestResSrc()
  if (alt) span.setAttribute('data-sn-media-alt', alt)
  if (title) span.setAttribute('data-sn-media-title', title)
  if (srcSet) span.setAttribute('data-sn-media-srcset', srcSet)
  if (bestResSrc) span.setAttribute('data-sn-media-bestres', bestResSrc)
  if (node.__maxWidth != null) span.setAttribute('data-sn-media-maxwidth', String(node.__maxWidth))
  if (node.__autolink) span.setAttribute('data-sn-media-autolink', 'true')
  // dimensions are only meaningful once the media check has resolved the kind;
  // unknown exports render as links
  if (kind !== 'unknown') {
    const { width, height } = node.getWidthAndHeight() || {}
    width && span.style.setProperty('--width', width)
    height && span.style.setProperty('--height', height)
  }
  return span
}

export class MediaNode extends DecoratorNode {
  __src
  __title
  __alt
  __maxWidth
  __autolink

  $config () {
    return this.config('media', {
      extends: DecoratorNode,
      stateConfigs: [
        { flat: true, stateConfig: kindState },
        { flat: true, stateConfig: statusState },
        { flat: true, stateConfig: srcSetState },
        { flat: true, stateConfig: bestResSrcState },
        { flat: true, stateConfig: widthState },
        { flat: true, stateConfig: heightState }
      ]
    })
  }

  constructor (src, title, alt, maxWidth, autolink, key) {
    super(key)
    this.__src = src
    this.__title = title ?? ''
    this.__alt = alt ?? ''
    this.__maxWidth = maxWidth ?? 500
    this.__autolink = autolink ?? false
  }

  static clone (node) {
    const clone = new MediaNode(
      node.__src,
      node.__title,
      node.__alt,
      node.__maxWidth,
      node.__autolink,
      node.__key
    )
    return clone
  }

  static importJSON (serializedNode) {
    const { src, srcSet, bestResSrc, title, alt, width, height, maxWidth, kind, status, autolink } = serializedNode
    const node = $createMediaNode({ src, title, alt, width, height, maxWidth, autolink })
    $setState(node, kindState, kind ?? 'unknown')
    $setState(node, statusState, status ?? 'idle')
    $setState(node, srcSetState, srcSet ?? null)
    $setState(node, bestResSrcState, bestResSrc ?? null)
    $setState(node, widthState, width ?? null)
    $setState(node, heightState, height ?? null)
    return node
  }

  exportJSON () {
    return {
      ...super.exportJSON(),
      src: this.__src,
      srcSet: $getState(this, srcSetState),
      bestResSrc: $getState(this, bestResSrcState),
      title: this.__title,
      alt: this.__alt,
      width: $getState(this, widthState),
      height: $getState(this, heightState),
      maxWidth: this.__maxWidth,
      kind: $getState(this, kindState),
      status: $getState(this, statusState),
      autolink: this.__autolink
    }
  }

  static importDOM () {
    return {
      img: () => ({
        conversion: $convertMediaElement,
        priority: 0
      }),
      video: () => ({
        conversion: $convertMediaElement,
        priority: 0
      }),
      span: (domNode) => {
        if (!domNode.hasAttribute('data-sn-media-src')) {
          return null
        }
        return {
          conversion: $convertMediaElement,
          priority: 1
        }
      }
    }
  }

  exportDOM (editor) {
    const span = createMediaSpan(this, editor._config.theme)

    if (this.getKind() === 'unknown') {
      // export a link instead of media if we don't know the type
      const link = document.createElement('a')
      link.setAttribute('href', this.__src)
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener nofollow noreferrer')
      link.className = 'sn-media-autolink__loading'
      link.textContent = this.__src
      span.appendChild(link)
      return { element: span }
    }

    const loading = document.createElement('span')
    loading.className = 'sn-media__loading'
    span.appendChild(loading)

    return { element: span }
  }

  createDOM (config) {
    return createMediaSpan(this, config.theme)
  }

  updateDOM () {
    return false
  }

  getSrc () {
    return this.__src
  }

  getSrcSet () {
    return $getState(this, srcSetState)
  }

  getBestResSrc () {
    return $getState(this, bestResSrcState)
  }

  setBestResSrc (bestResSrc) {
    $setState(this, bestResSrcState, bestResSrc ?? null)
  }

  setSrcSet (srcSet) {
    $setState(this, srcSetState, srcSet ?? null)
  }

  getAlt () {
    return this.__alt
  }

  getTitle () {
    return this.__title
  }

  getKind () {
    return $getState(this, kindState)
  }

  getStatus () {
    return $getState(this, statusState)
  }

  getWidthAndHeight () {
    return { width: $getState(this, widthState), height: $getState(this, heightState) }
  }

  setWidthAndHeight (width, height) {
    $setState(this, widthState, width)
    $setState(this, heightState, height)
  }

  setKind (kind) {
    $setState(this, kindState, kind)
  }

  setStatus (status) {
    $setState(this, statusState, status)
  }

  isAutolink () {
    return this.__autolink
  }

  // shortcut for setting kind and status via media check
  applyCheckResult (kind) {
    $setState(this, kindState, kind)
    $setState(this, statusState, kind === 'unknown' ? 'error' : 'done')
  }

  isInline () {
    return false
  }

  getTextContent () {
    return this.__src
  }

  decorate () {
    const MediaComponent = require('@/components/editor/nodes/media').default
    return (
      <MediaComponent
        src={this.__src}
        srcSet={$getState(this, srcSetState)}
        bestResSrc={$getState(this, bestResSrcState)}
        title={this.__title}
        alt={this.__alt}
        kind={$getState(this, kindState)}
        status={$getState(this, statusState)}
        width={$getState(this, widthState)}
        height={$getState(this, heightState)}
        maxWidth={this.__maxWidth}
        autolink={this.__autolink}
        nodeKey={this.getKey()}
      />
    )
  }
}

export function $createMediaNode ({ src, title, alt, width, height, maxWidth, autolink, key, srcSet, bestResSrc, kind, status }) {
  const node = new MediaNode(
    src,
    title,
    alt,
    maxWidth ? Math.min(maxWidth, 500) : Math.min(width ?? 320, 500),
    autolink,
    key
  )
  if (kind) {
    node.setKind(kind)
  }
  if (status) {
    node.setStatus(status)
  }
  if (width && height) {
    node.setWidthAndHeight(width, height)
  }
  if (srcSet) {
    node.setSrcSet(srcSet)
  }
  // if imgproxy didn't return a bestResSrc, use the original src
  // this is to ensure the media is displayed even if imgproxy is not available
  node.setBestResSrc(bestResSrc ?? src)
  return $applyNodeReplacement(node)
}

export function $isMediaNode (node) {
  return node instanceof MediaNode
}
